'use client';

import {
  faImage,
  faImages,
  faMagnifyingGlassPlus,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { faGripDots } from '@fortawesome/pro-solid-svg-icons';
import { motion, Reorder, useDragControls } from 'framer-motion';
import { ChangeEventHandler, DragEventHandler, FC, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Divider,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from '@/components/ui';
import { Icon } from '@/components/ui/icon/icon';
import { RenderableImage } from '@/components/ui/image/renderable-image';
import { cn } from '@/utils';
import { StorageGalleryModal } from './storage-gallery-modal';

export type MediaDragAndDropProps = {
  value: string[];
  onChange: (urls: string[]) => void;
  title?: string;
  description?: string;
  buttonText?: string;
  error?: boolean;
  errorMessage?: string;
  singleFile?: boolean;
  disabled?: boolean;
  /** When true, show "Select from gallery" and use storage list API. Default true. */
  allowGallery?: boolean;
  /** When true, only accept image files. Default true. */
  acceptOnlyImages?: boolean;
  /** Maximum number of files allowed. Omit for unlimited. */
  maxFiles?: number;
  /** Explicit allowlist of accepted MIME types (e.g. `['image/jpeg', 'image/png']`). When set, files outside this list are rejected with `unsupportedFormatMessage`. */
  acceptedTypes?: string[];
  /** Toast shown when a file outside `acceptedTypes` is dropped/picked. Defaults to a generic English message. */
  unsupportedFormatMessage?: string;
  /** Called when the user adds new local files. Provides the File together with the blob URL used in `value`. */
  onFilesAdded?: (entries: { file: File; url: string }[]) => void;
};

type MediaItem = {
  id: string;
  url: string;
};

type MediaThumbnailItemProps = {
  item: MediaItem;
  index: number;
  disabled: boolean;
  onRemove: (index: number) => void;
  onZoom: (url: string) => void;
};

const MediaThumbnailItem = ({
  item,
  index,
  disabled,
  onRemove,
  onZoom,
}: MediaThumbnailItemProps) => {
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={dragControls}
      className="relative h-[125px] w-[125px] shrink-0 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-100"
    >
      <RenderableImage
        src={item.url}
        alt={`Image ${index + 1}`}
        className="h-full w-full object-cover"
      />
      <IconButton
        size="sm"
        isIconOnly
        className="absolute top-1 right-1 z-10 bg-white/90 shadow-sm hover:bg-zinc-200"
        onPress={() => onRemove(index)}
        disabled={disabled}
        aria-label="Remove"
      >
        <Icon icon={faTimes} />
      </IconButton>
      <IconButton
        size="sm"
        isIconOnly
        className="absolute top-1 left-1 z-10 bg-white/90 shadow-sm hover:bg-zinc-200"
        onPress={() => onZoom(item.url)}
        aria-label="Zoom"
      >
        <Icon icon={faMagnifyingGlassPlus} />
      </IconButton>
      <IconButton
        size="sm"
        isIconOnly
        className="absolute bottom-1 left-1 z-10 cursor-grab bg-white/90 shadow-sm hover:bg-zinc-200 active:cursor-grabbing"
        onPointerDown={e => {
          e.preventDefault();
          dragControls.start(e);
        }}
        aria-label="Drag to reorder"
      >
        <Icon icon={faGripDots} />
      </IconButton>
    </Reorder.Item>
  );
};

const syncMediaItems = (urls: string[], prev: MediaItem[]): MediaItem[] => {
  const used = new Set<number>();
  return urls.map(url => {
    const matchIndex = prev.findIndex((item, index) => item.url === url && !used.has(index));
    if (matchIndex >= 0) {
      used.add(matchIndex);
      return prev[matchIndex];
    }
    return { id: crypto.randomUUID(), url };
  });
};

const MediaDragAndDrop: FC<MediaDragAndDropProps> = ({
  value,
  onChange,
  title = 'Images',
  description = 'Drag and drop images here, or click to upload.',
  buttonText = 'Upload images',
  error = false,
  errorMessage,
  singleFile = false,
  disabled = false,
  allowGallery = true,
  acceptOnlyImages = true,
  maxFiles,
  acceptedTypes,
  unsupportedFormatMessage,
  onFilesAdded,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>(() =>
    value.map(url => ({ id: crypto.randomUUID(), url })),
  );
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const commitUrls = (urls: string[]) => {
    setMediaItems(prev => syncMediaItems(urls, prev));
    onChange(urls);
  };

  const handleDrag: DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);
    const typeFiltered = acceptedTypes
      ? filesArray.filter(f => acceptedTypes.includes(f.type))
      : acceptOnlyImages
        ? filesArray.filter(f => f.type.startsWith('image/'))
        : filesArray;
    if (typeFiltered.length < filesArray.length) {
      toast.error(
        acceptedTypes
          ? (unsupportedFormatMessage ?? 'Unsupported file format.')
          : 'Only image files are accepted.',
      );
      return;
    }

    const newEntries = typeFiltered.map(file => ({ file, url: URL.createObjectURL(file) }));
    const newUrls = newEntries.map(entry => entry.url);
    const merged = singleFile ? newUrls : [...mediaItems.map(item => item.url), ...newUrls];
    const next = maxFiles ? merged.slice(0, maxFiles) : merged;
    if (maxFiles && merged.length > maxFiles) {
      toast.info(`Maximum ${maxFiles} images allowed.`);
    }
    const accepted = newEntries.filter(entry => next.includes(entry.url));
    if (accepted.length > 0) onFilesAdded?.(accepted);
    commitUrls(next);
  };

  const handleDrop: DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = e => {
    handleFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = (index: number) => {
    const next = mediaItems.filter((_, i) => i !== index).map(item => item.url);
    commitUrls(next);
  };

  const handleReorder = (newOrder: MediaItem[]) => {
    setMediaItems(newOrder);
    onChange(newOrder.map(item => item.url));
  };

  const handleGallerySave = (urls: string[]) => {
    const merged = singleFile ? urls.slice(0, 1) : urls;
    const next = maxFiles ? merged.slice(0, maxFiles) : merged;
    if (maxFiles && merged.length > maxFiles) {
      toast.info(`Maximum ${maxFiles} images allowed.`);
    }
    commitUrls(next);
  };

  useEffect(() => {
    setMediaItems(prev => syncMediaItems(value, prev));
  }, [value]);

  return (
    <div className="flex flex-col gap-1">
      {title ? <span className="text-sm text-zinc-600">{title}</span> : null}
      <div
        className={cn(
          'rounded-lg border-2 border-dashed p-6 text-center transition-colors',
          isDragActive && 'border-primary bg-default-100',
          error && 'border-danger',
          !isDragActive && !error && 'border-zinc-300',
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Icon icon={faImage} className="mx-auto text-3xl text-zinc-400" />
        <p className="mt-2 text-sm text-zinc-500">{description}</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <Button
            variant="bordered"
            size="sm"
            disabled={disabled}
            onPress={() => fileInputRef.current?.click()}
            startContent={<Icon icon={faImage} className="text-sm" />}
          >
            {buttonText}
          </Button>
          {allowGallery && (
            <>
              <Divider orientation="vertical" className="h-6" />
              <Button
                variant="bordered"
                size="sm"
                disabled={disabled}
                onPress={() => setIsGalleryOpen(true)}
                startContent={<Icon icon={faImages} className="text-sm" />}
              >
                Select from gallery
              </Button>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={
            acceptedTypes && acceptedTypes.length > 0
              ? acceptedTypes.join(',')
              : acceptOnlyImages
                ? 'image/*'
                : '*'
          }
          multiple={!singleFile}
          className="hidden"
          onChange={handleFileChange}
        />

        {allowGallery && (
          <StorageGalleryModal
            isOpen={isGalleryOpen}
            onClose={() => setIsGalleryOpen(false)}
            onSave={handleGallerySave}
            selectedUrls={mediaItems.map(item => item.url)}
            multiple={!singleFile}
            disabled={disabled}
          />
        )}

        {mediaItems.length > 0 && (
          <motion.div
            initial={false}
            animate={{ height: 'auto', opacity: 1 }}
            className="mt-4 overflow-x-auto pb-2"
          >
            <p className="mb-2 text-left text-sm text-zinc-600">Current images</p>
            <Reorder.Group
              axis="x"
              values={mediaItems}
              onReorder={handleReorder}
              className="mx-auto flex list-none gap-4"
              style={{ width: 'max-content' }}
            >
              {mediaItems.map((item, index) => (
                <MediaThumbnailItem
                  key={item.id}
                  item={item}
                  index={index}
                  disabled={disabled}
                  onRemove={handleRemove}
                  onZoom={setSelectedImage}
                />
              ))}
            </Reorder.Group>
          </motion.div>
        )}
      </div>

      {error && errorMessage ? <p className="text-danger mt-1 text-sm">{errorMessage}</p> : null}

      <Modal isOpen={!!selectedImage} onOpenChange={open => !open && setSelectedImage(null)}>
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">Preview</ModalHeader>
          <ModalBody>
            {selectedImage ? (
              <RenderableImage
                src={selectedImage}
                alt="Preview"
                className="max-h-[80vh] w-full object-contain"
              />
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default MediaDragAndDrop;
